// src/components/user/AssetDetailsModal.jsx

import React from "react";
import {
  Box, AppBar, Toolbar, IconButton, Typography, Chip, Grid,
  Card, CardContent, Stack, Tooltip,
  Container, Dialog, Slide, Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import CloseIcon          from "@mui/icons-material/Close";
import Inventory2Icon     from "@mui/icons-material/Inventory2";
import QrCodeIcon         from "@mui/icons-material/QrCode";
import InfoOutlinedIcon   from "@mui/icons-material/InfoOutlined";
import PlaceOutlinedIcon  from "@mui/icons-material/PlaceOutlined";
import PersonOutlineIcon  from "@mui/icons-material/PersonOutline";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import MemoryIcon         from "@mui/icons-material/Memory";
import BuildIcon          from "@mui/icons-material/Build";
import QrCode2Icon        from "@mui/icons-material/QrCode2";
import BusinessIcon       from "@mui/icons-material/Business";
import ReceiptLongIcon    from "@mui/icons-material/ReceiptLong";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import WarningAmberIcon   from "@mui/icons-material/WarningAmber";
import AccessTimeIcon     from "@mui/icons-material/AccessTime";
import BoxIcon            from "@mui/icons-material/Category";

/* ═══════════════════════════════════════════════════════════════════════════
   TRANSITION
═══════════════════════════════════════════════════════════════════════════ */

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/* ═══════════════════════════════════════════════════════════════════════════
   STYLED HELPERS (matching CreateEditAssetModal)
═══════════════════════════════════════════════════════════════════════════ */

const SectionCard = styled(Card)(() => ({
  borderRadius: 8,
  border: "1px solid #e2e8f0",
  height: "100%",
  display: "flex",
  flexDirection: "column",
}));

const SectionTitle = styled(Typography)(() => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: "0.9375rem",
  fontWeight: 700,
  color: "#1e293b",
  marginBottom: "1.25rem",
  paddingBottom: "0.75rem",
  borderBottom: "1px solid #e5e7eb",
}));

const FieldsGrid = styled(Box)(() => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "1.5rem 1rem",
  width: "100%",
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.025em",
  marginBottom: 4,
}));

const FieldValue = styled(Typography)(() => ({
  fontSize: "0.9rem",
  fontWeight: 500,
  color: "#111827",
  wordBreak: "break-word",
}));

const MonoValue = styled(FieldValue)(() => ({
  fontFamily: "monospace",
  fontWeight: 700,
}));

const StatusChip = styled(Chip)(() => ({
  fontWeight: 700,
  fontSize: "0.72rem",
}));

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════════════════════ */

const AssetDetailsModal = ({ asset, onClose }) => {
  if (!asset) return null;

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const statusChipProps = (status) => {
    const key = String(status || "").toLowerCase();
    if (["assigned"].includes(key)) return { color: "primary", variant: "filled" };
    if (["active", "in use"].includes(key)) return { color: "success", variant: "filled" };
    if (["available"].includes(key)) return { color: "info",    variant: "filled" };
    if (["maintenance", "in repair", "under maintenance"].includes(key)) return { color: "warning", variant: "filled" };
    if (["retired", "disposed", "lost", "stolen"].includes(key)) return { color: "error",   variant: "filled" };
    return { color: "default", variant: "filled" };
  };

  /* ══════════════════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════════════════ */
  return (
    <Dialog
      fullScreen
      open={Boolean(asset)}
      onClose={onClose}
      TransitionComponent={Transition}
      PaperProps={{
        sx: { display: "flex", flexDirection: "column", overflow: "hidden" },
      }}
    >
      {/* ── APP BAR ── */}
      <AppBar
        sx={{ position: "relative", flexShrink: 0, bgcolor: "#2563eb" }}
        elevation={1}
      >
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>

          <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Inventory2Icon fontSize="small" />
              <Typography variant="h6" sx={{ lineHeight: 1.2 }} noWrap>
                Asset Details: {asset.name || "Unnamed"}
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              <MonoValue
                component="span"
                sx={{ color: "#fff", fontFamily: "monospace", fontSize: "0.75rem" }}
              >
                {asset.assetId || "—"}
              </MonoValue>
              {asset.qrCodeValue && (
                <Tooltip title="QR Code available">
                  <QrCodeIcon sx={{ fontSize: 14, ml: 1, verticalAlign: "middle" }} />
                </Tooltip>
              )}
            </Typography>
          </Box>

          {asset.status && (
            <StatusChip
              label={String(asset.status)}
              {...statusChipProps(asset.status)}
              sx={{
                height: 22,
                fontSize: "0.7rem",
                bgcolor: "rgba(255,255,255,0.25)",
                color: "#fff",
              }}
            />
          )}
        </Toolbar>
      </AppBar>

      {/* ── BODY + FOOTER ── */}
      <Box sx={{ display: "flex", flexDirection: "column", bgcolor: "#f1f5f9", flex: 1, minHeight: 0, overflow: "hidden" }}>
        
        {/* ── QUICK INFO STRIP ── */}
        <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e2e8f0", px: { xs: 2, md: 4 }, py: 2, flexShrink: 0 }}>
          <Grid container spacing={2}>
            {[
              { label: "Status",   value: asset.status },
              { label: "Category", value: asset.category },
              { label: "Location", value: asset.location },
              { label: "Value",    value: formatCurrency(asset.purchaseValue) },
            ].map(({ label, value }) => (
              <Grid item xs={12} sm={6} md={3} key={label}>
                <FieldLabel>{label}</FieldLabel>
                {label === "Status" ? (
                  <StatusChip
                    label={String(value || "—")}
                    {...statusChipProps(value)}
                    size="small"
                    sx={{ mt: 0.25 }}
                  />
                ) : (
                  <FieldValue>{String(value || "—")}</FieldValue>
                )}
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* ── SCROLLABLE CONTENT ── */}
        <Box sx={{ flex: 1, overflowY: "auto", py: { xs: 2, md: 4 } }}>
          <Container maxWidth="lg">
            <Stack spacing={3}>

              {/* SECTION 1: ASSET & ASSIGNMENT INFO */}
              <Grid container spacing={3} alignItems="stretch">
                <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                  <SectionCard variant="outlined" sx={{ width: "100%" }}>
                    <CardContent sx={{ p: 3 }}>
                      <SectionTitle>
                        <BoxIcon fontSize="small" sx={{ color: "#2563eb" }} />
                        Asset Information
                      </SectionTitle>
                      <FieldsGrid>
                        <Box>
                          <FieldLabel>Asset ID</FieldLabel>
                          <MonoValue>{asset.assetId || "—"}</MonoValue>
                          {asset.qrCodeValue && (
                            <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.5 }}>
                              QR: {asset.qrCodeValue}
                            </Typography>
                          )}
                        </Box>
                        <Box>
                          <FieldLabel>Asset Name</FieldLabel>
                          <FieldValue>{asset.name || "—"}</FieldValue>
                        </Box>
                        <Box>
                          <FieldLabel>Category</FieldLabel>
                          <FieldValue>
                            {asset.category || "—"}
                            {asset.categoryCode && <span style={{ color: "#6b7280", marginLeft: 4 }}>({asset.categoryCode})</span>}
                          </FieldValue>
                        </Box>
                        {asset.subCategory && (
                          <Box>
                            <FieldLabel>Sub Category</FieldLabel>
                            <FieldValue>{asset.subCategory}</FieldValue>
                          </Box>
                        )}
                        <Box>
                          <FieldLabel>Brand</FieldLabel>
                          <FieldValue>{asset.brand || "—"}</FieldValue>
                        </Box>
                        {asset.model && (
                          <Box>
                            <FieldLabel>Model</FieldLabel>
                            <FieldValue>{asset.model}</FieldValue>
                          </Box>
                        )}
                      </FieldsGrid>
                    </CardContent>
                  </SectionCard>
                </Grid>

                <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                  <SectionCard variant="outlined" sx={{ width: "100%" }}>
                    <CardContent sx={{ p: 3 }}>
                      <SectionTitle>
                        <PersonOutlineIcon fontSize="small" sx={{ color: "#2563eb" }} />
                        Assignment Details
                      </SectionTitle>
                      <FieldsGrid>
                        <Box>
                          <FieldLabel>Assigned To</FieldLabel>
                          <FieldValue>{asset.assignedToName || "—"}</FieldValue>
                        </Box>
                        <Box>
                          <FieldLabel>Assigned On</FieldLabel>
                          <FieldValue>{formatDate(asset.assignedOn)}</FieldValue>
                        </Box>
                        <Box>
                          <FieldLabel>Assigned By</FieldLabel>
                          <FieldValue>{asset.assignedBy || "—"}</FieldValue>
                        </Box>
                        <Box>
                          <FieldLabel>Current Status</FieldLabel>
                          <StatusChip label={String(asset.status || "—")} {...statusChipProps(asset.status)} size="small" />
                        </Box>
                        <Box>
                          <FieldLabel>Department</FieldLabel>
                          <FieldValue>{asset.department || "—"}</FieldValue>
                        </Box>
                        <Box>
                          <FieldLabel>Asset Created</FieldLabel>
                          <FieldValue>{formatDate(asset.createdAt)}</FieldValue>
                        </Box>
                      </FieldsGrid>
                    </CardContent>
                  </SectionCard>
                </Grid>
              </Grid>

              {/* SECTION 2: DESCRIPTION */}
              {asset.description && (
                <SectionCard variant="outlined">
                  <CardContent sx={{ p: 3 }}>
                    <SectionTitle>
                      <ChatBubbleOutlineIcon fontSize="small" sx={{ color: "#2563eb" }} />
                      Description
                    </SectionTitle>
                    <Typography sx={{ whiteSpace: "pre-wrap", color: "#111827", fontSize: "0.9rem" }}>
                      {asset.description}
                    </Typography>
                  </CardContent>
                </SectionCard>
              )}

              {/* SECTION 3: TECHNICAL & MAINTENANCE */}
              <Grid container spacing={3} alignItems="stretch">
                <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <SectionCard variant="outlined">
                    <CardContent sx={{ p: 3 }}>
                      <SectionTitle>
                        <MemoryIcon fontSize="small" sx={{ color: "#2563eb" }} />
                        Technical Specifications
                      </SectionTitle>
                      <FieldsGrid>
                        {asset.serialNumber !== "N/A" && asset.serialNumber && (
                          <Box>
                            <FieldLabel>Serial Number</FieldLabel>
                            <MonoValue>{asset.serialNumber}</MonoValue>
                          </Box>
                        )}
                        {asset.model && (
                          <Box>
                            <FieldLabel>Model</FieldLabel>
                            <FieldValue>{asset.model}</FieldValue>
                          </Box>
                        )}
                        <Box>
                          <FieldLabel>Condition</FieldLabel>
                          <Chip
                            label={String(asset.condition || "—")}
                            size="small"
                            color={asset.condition === "Good" ? "success" : "warning"}
                            sx={{ fontWeight: 700 }}
                          />
                        </Box>
                      </FieldsGrid>
                    </CardContent>
                  </SectionCard>

                  {asset.specifications && Object.keys(asset.specifications).length > 0 && (
                    <SectionCard variant="outlined">
                      <CardContent sx={{ p: 3 }}>
                        <SectionTitle>Additional Specifications</SectionTitle>
                        <FieldsGrid>
                          {Object.entries(asset.specifications).map(([key, value]) => (
                            <Box key={key}>
                              <FieldLabel>{key.charAt(0).toUpperCase() + key.slice(1)}</FieldLabel>
                              <FieldValue>{String(value)}</FieldValue>
                            </Box>
                          ))}
                        </FieldsGrid>
                      </CardContent>
                    </SectionCard>
                  )}
                </Grid>

                <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <SectionCard variant="outlined">
                    <CardContent sx={{ p: 3 }}>
                      <SectionTitle>
                        <BuildIcon fontSize="small" sx={{ color: "#2563eb" }} />
                        Maintenance & Warranty
                      </SectionTitle>
                      <FieldsGrid>
                        <Box>
                          <FieldLabel>Purchase Date</FieldLabel>
                          <FieldValue>{formatDate(asset.purchaseDate)}</FieldValue>
                        </Box>
                        {asset.warrantyExpiry && (
                          <Box>
                            <FieldLabel>Warranty Expiry</FieldLabel>
                            <FieldValue
                              sx={{
                                color: new Date(asset.warrantyExpiry) < new Date() ? "#dc2626" : "#16a34a",
                                display: "flex", alignItems: "center", gap: 0.5,
                              }}
                            >
                              {formatDate(asset.warrantyExpiry)}
                              {new Date(asset.warrantyExpiry) < new Date() && (
                                <WarningAmberIcon sx={{ fontSize: 16 }} titleAccess="Warranty expired" />
                              )}
                            </FieldValue>
                          </Box>
                        )}
                        {asset.lastMaintenance && (
                          <Box>
                            <FieldLabel>Last Maintenance</FieldLabel>
                            <FieldValue>{formatDate(asset.lastMaintenance)}</FieldValue>
                          </Box>
                        )}
                        {asset.nextMaintenance && (
                          <Box>
                            <FieldLabel>Next Maintenance</FieldLabel>
                            <FieldValue
                              sx={{
                                color: new Date(asset.nextMaintenance) < new Date() ? "#dc2626" : "inherit",
                                display: "flex", alignItems: "center", gap: 0.5,
                              }}
                            >
                              {formatDate(asset.nextMaintenance)}
                              {new Date(asset.nextMaintenance) < new Date() && (
                                <WarningAmberIcon sx={{ fontSize: 16 }} titleAccess="Maintenance overdue" />
                              )}
                            </FieldValue>
                          </Box>
                        )}
                      </FieldsGrid>
                    </CardContent>
                  </SectionCard>

                  {(asset.qrCodeValue || asset.barCode) && (
                    <SectionCard variant="outlined">
                      <CardContent sx={{ p: 3 }}>
                        <SectionTitle>
                          <QrCode2Icon fontSize="small" sx={{ color: "#2563eb" }} />
                          Identification Codes
                        </SectionTitle>
                        <FieldsGrid>
                          {asset.qrCodeValue && (
                            <Box>
                              <FieldLabel>QR Code</FieldLabel>
                              <MonoValue>{asset.qrCodeValue}</MonoValue>
                            </Box>
                          )}
                          {asset.barCode && (
                            <Box>
                              <FieldLabel>Bar Code</FieldLabel>
                              <MonoValue>{asset.barCode}</MonoValue>
                            </Box>
                          )}
                        </FieldsGrid>
                      </CardContent>
                    </SectionCard>
                  )}
                </Grid>
              </Grid>

              {/* SECTION 4: LOCATION & ORGANIZATION */}
              <Grid container spacing={3} alignItems="stretch">
                <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                  <SectionCard variant="outlined" sx={{ width: "100%" }}>
                    <CardContent sx={{ p: 3 }}>
                      <SectionTitle>
                        <PlaceOutlinedIcon fontSize="small" sx={{ color: "#2563eb" }} />
                        Current Location
                      </SectionTitle>
                      <FieldsGrid>
                        <Box>
                          <FieldLabel>Location Name</FieldLabel>
                          <FieldValue sx={{ fontWeight: 600 }}>{asset.location || "—"}</FieldValue>
                        </Box>
                        {asset.locationCode && (
                          <Box>
                            <FieldLabel>Location Code</FieldLabel>
                            <MonoValue>{asset.locationCode}</MonoValue>
                          </Box>
                        )}
                        <Box>
                          <FieldLabel>Full Address</FieldLabel>
                          <FieldValue>{asset.locationAddress || "—"}</FieldValue>
                        </Box>
                        <Box>
                          <FieldLabel>Department</FieldLabel>
                          <FieldValue>{asset.department || "—"}</FieldValue>
                        </Box>
                      </FieldsGrid>
                    </CardContent>
                  </SectionCard>
                </Grid>

                <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <SectionCard variant="outlined">
                    <CardContent sx={{ p: 3 }}>
                      <SectionTitle>
                        <BusinessIcon fontSize="small" sx={{ color: "#2563eb" }} />
                        Organization
                      </SectionTitle>
                      <FieldsGrid>
                        <Box>
                          <FieldLabel>Company</FieldLabel>
                          <FieldValue>{asset.originalData?.company || "Current Company"}</FieldValue>
                        </Box>
                        <Box>
                          <FieldLabel>Responsible Person</FieldLabel>
                          <FieldValue>{asset.assignedToName || "—"}</FieldValue>
                        </Box>
                        <Box>
                          <FieldLabel>Assignment Date</FieldLabel>
                          <FieldValue>{formatDate(asset.assignedOn)}</FieldValue>
                        </Box>
                      </FieldsGrid>
                    </CardContent>
                  </SectionCard>

                  <Box sx={{ display: "flex", gap: 1.5, p: 2, bgcolor: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 2, alignItems: "flex-start" }}>
                    <InfoOutlinedIcon sx={{ color: "#2563eb", mt: 0.25 }} />
                    <Typography variant="body2" color="#1e3a8a">
                      <strong>Important:</strong> Please notify your administrator if you need to relocate this asset or if there are any changes to its current location.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* SECTION 5: FINANCIAL & RESPONSIBILITY */}
              <Grid container spacing={3} alignItems="stretch">
                <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                  <SectionCard variant="outlined" sx={{ width: "100%" }}>
                    <CardContent sx={{ p: 3 }}>
                      <SectionTitle>
                        <ReceiptLongIcon fontSize="small" sx={{ color: "#2563eb" }} />
                        Purchase Information
                      </SectionTitle>
                      <FieldsGrid>
                        <Box>
                          <FieldLabel>Purchase Value</FieldLabel>
                          <FieldValue sx={{ color: "#16a34a", fontWeight: 700, fontSize: "1rem" }}>
                            {formatCurrency(asset.purchaseValue)}
                          </FieldValue>
                        </Box>
                        <Box>
                          <FieldLabel>Purchase Date</FieldLabel>
                          <FieldValue>{formatDate(asset.purchaseDate)}</FieldValue>
                        </Box>
                        {asset.invoiceNumber !== "N/A" && asset.invoiceNumber && (
                          <Box>
                            <FieldLabel>Invoice Number</FieldLabel>
                            <MonoValue>{asset.invoiceNumber}</MonoValue>
                          </Box>
                        )}
                        <Box>
                          <FieldLabel>Added to System</FieldLabel>
                          <FieldValue>{formatDate(asset.createdAt)}</FieldValue>
                        </Box>
                        <Box>
                          <FieldLabel>Added By</FieldLabel>
                          <FieldValue>{asset.createdBy || "—"}</FieldValue>
                        </Box>
                      </FieldsGrid>
                    </CardContent>
                  </SectionCard>
                </Grid>

                <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                  <SectionCard variant="outlined" sx={{ width: "100%" }}>
                    <CardContent sx={{ p: 3 }}>
                      <SectionTitle>
                        <ShieldOutlinedIcon fontSize="small" sx={{ color: "#2563eb" }} />
                        Asset Responsibility
                      </SectionTitle>

                      <Box sx={{ p: 2, bgcolor: "#fffbeb", border: "1px solid #fde68a", borderRadius: 2, mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 1, color: "#92400e", fontWeight: 700, mb: 1 }}>
                          <WarningAmberIcon fontSize="small" />
                          Your Responsibility
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5, color: "#78350f", fontSize: "0.875rem", "& li": { mb: 0.5 } }}>
                          <li>You are responsible for this asset valued at <strong>{formatCurrency(asset.purchaseValue)}</strong></li>
                          <li>Report any damage, loss, or theft immediately</li>
                          <li>Keep the asset in good working condition</li>
                          <li>Return the asset when requested by administration</li>
                        </Box>
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 0.5 }}>
                          Need Help?
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Contact your administrator ({asset.createdBy || "Admin"}) for any issues related to this asset.
                        </Typography>
                      </Box>
                    </CardContent>
                  </SectionCard>
                </Grid>
              </Grid>

            </Stack>
          </Container>
        </Box>

        {/* ── STICKY FOOTER ── */}
        <Box sx={{ borderTop: "1px solid #e2e8f0", backgroundColor: "#fff", py: 2, px: 3, boxShadow: "0 -2px 8px rgba(0,0,0,0.04)", flexShrink: 0 }}>
          <Container maxWidth={false} disableGutters>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <AccessTimeIcon fontSize="small" sx={{ fontSize: 14 }} />
                Last updated: {formatDate(asset.updatedAt || asset.createdAt)} by {asset.updatedBy || "—"}
              </Typography>

              <Button
                onClick={onClose}
                variant="outlined"
                sx={{
                  px: 3, py: 1, textTransform: "none", fontWeight: 600, borderRadius: "6px",
                  borderColor: "#d1d5db", color: "#374151", "&:hover": { backgroundColor: "#f3f4f6" },
                }}
              >
                Close
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>
    </Dialog>
  );
};

export default AssetDetailsModal;